import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Paper, TextField, Typography, MenuItem, Snackbar, Alert, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Stack, IconButton } from '@mui/material';
import { CalendarMonthOutlined as CalendarIcon, AddOutlined as AddIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../apiBase';
import { useAuth } from '../context/AuthContext';

export default function ManualHeadOfficeTransaction() {
  const { user } = useAuth();
  const role = user?.role || '';
  const isSuperAdminRole = role === 'superadmin';
  const permissionsMap = user?.page_permissions_map || {};
  const pagePerm =
    permissionsMap.manual_head_office_transaction ||
    permissionsMap.manual_head_office ||
    {};
  const canCreate = isSuperAdminRole || Boolean(pagePerm.can_create);

  const dateRef = useRef(null);
  const [vendors, setVendors] = useState([]);
  const [txnTypes, setTxnTypes] = useState([]);
  const [appCodes, setAppCodes] = useState([]);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [toBankOptions, setToBankOptions] = useState([]);
  const [fromBankOptions, setFromBankOptions] = useState([]);
  const [form, setForm] = useState({
    userCode: '',
    date: '',
    fromVendorCode: '',
    fromBankCode: '',
    toVendorCode: '',
    toBankCode: '',
    amountPaid: '',
    cashReceived: '',
    oldCashBalance: '',
    commissionPct: '',
    adjustments: '',
    txnType: '',
    siteContactPerson: '',
    rectification: '',
    purpose: '',
  });
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [purposeDialogOpen, setPurposeDialogOpen] = useState(false);
  const [purposeForm, setPurposeForm] = useState({ fromCode: '', toCode: '', purpose: '' });
  
  // New Transaction Type State
  const [addTxnTypeOpen, setAddTxnTypeOpen] = useState(false);
  const [newTxnType, setNewTxnType] = useState('');

  useEffect(() => {
    const load = async () => {
      let role = '';
      let localUserCode = '';
      try {
        const token = localStorage.getItem('token') || '';
        const meRes = await fetch(`${API_BASE_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        const meData = await meRes.json().catch(() => ({}));
        role = meData?.user?.role || '';
        const email = meData?.user?.email || '';
        // localUserCode = email ? String(email).split('@')[0] : '';
        const appCode = meData?.user?.application_code || '';
        const superFlag = role === 'superadmin';
        setIsSuperadmin(superFlag);
        if (!superFlag && appCode) {
          setForm((p) => ({ ...p, userCode: appCode }));
        }
      } catch {
      }
      try {
        const res = await fetch(`${API_BASE_URL}/api/vendors`, { headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } });
        const data = await res.json();
        let fetchedVendors = Array.isArray(data.items) ? data.items : [];
        const userAssociations = user?.association || [];
        if (role !== 'superadmin') {
           fetchedVendors = fetchedVendors.filter(v => userAssociations.includes(v.firmVendorCode));
        }
        const seen = new Set();
        const deduped = [];
        for (const v of fetchedVendors) {
          const code = String(v?.firmVendorCode || '').trim();
          if (!code || seen.has(code)) continue;
          seen.add(code);
          deduped.push(v);
        }
        setVendors(deduped);
      } catch (e) {
        setNotification({ open: true, message: 'Failed to load vendors', severity: 'error' });
      }
      try {
        const res2 = await fetch(`${API_BASE_URL}/api/transaction-types`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        });
        const data2 = await res2.json().catch(() => ([]));
        setTxnTypes(Array.isArray(data2) ? data2 : []);
      } catch (e) {
        setTxnTypes([]);
      }
      if (role === 'superadmin') {
        try {
          const res3 = await fetch(`${API_BASE_URL}/api/user-application-codes`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
          });
          const data3 = await res3.json().catch(() => ({}));
          const codes = Array.isArray(data3.items)
            ? data3.items.map((it) => it.code).filter(Boolean).sort()
            : [];
          setAppCodes(codes);
        } catch (e) {
          setAppCodes([]);
        }
      }
    };
    load();
  }, [user]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((p) => {
      const next = { ...p, [field]: value };
      if (field === 'fromVendorCode') {
        const v = vendors.find((x) => x.firmVendorCode === value);
        next.fromBankCode = '';
      }
      if (field === 'toVendorCode') {
        const v = vendors.find((x) => x.firmVendorCode === value);
        next.toBankCode = '';
        next.siteContactPerson = v?.vendorContactPerson || '';
        next.commissionPct = v?.applicableCommissionPct ?? '';
      }
      return next;
    });
    const fc = field === 'fromVendorCode' ? value : form.fromVendorCode;
    const tc = field === 'toVendorCode' ? value : form.toVendorCode;
    if (fc && tc) {
      lookupPurpose(fc, tc);
    } else {
      setForm((prev) => ({ ...prev, purpose: '' }));
    }
  };

  useEffect(() => {
    const loadFromBanks = async () => {
      const code = String(form.fromVendorCode || '').trim();
      if (!code) {
        setFromBankOptions([]);
        setForm((prev) => ({ ...prev, fromBankCode: '' }));
        return;
      }
      try {
        const res = await fetch(`${API_BASE_URL}/api/vendors/${encodeURIComponent(code)}/bank-accounts`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        });
        const data = await res.json();
        const items = Array.isArray(data.items) ? data.items : [];
        if (items.length > 0) {
          setFromBankOptions(items);
          const first = items[0]?.bankCode || '';
          setForm((prev) => ({ ...prev, fromBankCode: first }));
        } else {
          const v = vendors.find((x) => String(x.firmVendorCode || '').trim() === code);
          const fallback = v?.firmVendorBankCode || '';
          if (fallback) {
            setFromBankOptions([{ id: 'fallback', bankCode: fallback, bankName: 'Primary' }]);
          } else {
            setFromBankOptions([]);
          }
          setForm((prev) => ({ ...prev, fromBankCode: fallback }));
        }
      } catch (e) {
        setFromBankOptions([]);
        const v = vendors.find((x) => x.firmVendorCode === code);
        const fallback = v?.firmVendorBankCode || '';
        setForm((prev) => ({ ...prev, fromBankCode: fallback }));
      }
    };
    loadFromBanks();
  }, [form.fromVendorCode]);

  useEffect(() => {
    const loadBanks = async () => {
      const code = String(form.toVendorCode || '').trim();
      if (!code) {
        setToBankOptions([]);
        setForm((prev) => ({ ...prev, toBankCode: '' }));
        return;
      }
      try {
        const res = await fetch(`${API_BASE_URL}/api/vendors/${encodeURIComponent(code)}/bank-accounts`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        });
        const data = await res.json();
        const items = Array.isArray(data.items) ? data.items : [];
        if (items.length > 0) {
          setToBankOptions(items);
          const first = items[0]?.bankCode || '';
          setForm((prev) => ({ ...prev, toBankCode: first }));
        } else {
          const v = vendors.find((x) => String(x.firmVendorCode || '').trim() === code);
          const fallback = v?.firmVendorBankCode || '';
          if (fallback) {
            setToBankOptions([{ id: 'fallback', bankCode: fallback, bankName: 'Primary' }]);
          } else {
            setToBankOptions([]);
          }
          setForm((prev) => ({ ...prev, toBankCode: fallback }));
        }
      } catch (e) {
        setToBankOptions([]);
        const v = vendors.find((x) => x.firmVendorCode === code);
        const fallback = v?.firmVendorBankCode || '';
        setForm((prev) => ({ ...prev, toBankCode: fallback }));
      }
    };
    loadBanks();
  }, [form.toVendorCode]);
  const handleSaveTxnType = async () => {
    if (!newTxnType.trim()) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/transaction-types`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ transactionType: newTxnType }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add');

      setNotification({ open: true, message: 'Transaction Type Added', severity: 'success' });
      setAddTxnTypeOpen(false);
      setNewTxnType('');

      // Reload types
      const res2 = await fetch(`${API_BASE_URL}/api/transaction-types`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      const data2 = await res2.json();
      setTxnTypes(Array.isArray(data2) ? data2 : []);
      
      // Auto select
      setForm(p => ({ ...p, txnType: newTxnType }));

    } catch (e) {
      setNotification({ open: true, message: e.message, severity: 'error' });
    }
  };

  const lookupPurpose = async (fromCode, toCode) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/code-purpose?fromCode=${encodeURIComponent(fromCode)}&toCode=${encodeURIComponent(toCode)}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      const data = await res.json();
      const first = Array.isArray(data) ? data[0] : null;
      setForm((prev) => ({ ...prev, purpose: first?.purpose || '' }));
    } catch {
      setForm((prev) => ({ ...prev, purpose: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.userCode || !form.date) {
      setNotification({ open: true, message: 'User Code and Date required', severity: 'error' });
      return;
    }
    if (!canCreate) {
      setNotification({ open: true, message: 'You do not have create permission for this page', severity: 'error' });
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/office-transactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setNotification({ open: true, message: 'Transaction saved', severity: 'success' });
      setForm({
        userCode: isSuperadmin ? '' : (user?.application_code || ''),
        date: '',
        fromVendorCode: '',
        fromBankCode: '',
        toVendorCode: '',
        toBankCode: '',
        amountPaid: '',
        cashReceived: '',
        oldCashBalance: '',
        commissionPct: '',
        adjustments: '',
        txnType: '',
        siteContactPerson: '',
        rectification: '',
        purpose: '',
      });
    } catch (err) {
      setNotification({ open: true, message: err.message, severity: 'error' });
    }
  };

  return (
    <Layout title="Manual Head Office Transaction">
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <Typography variant="h4" align="center" sx={{ fontWeight: 500, letterSpacing: 0.5, mb: 4 }}>
          Manual Head Office Transaction
        </Typography>

        <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, boxShadow: '0 18px 45px rgba(15,23,42,0.08)' }}>
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
              {isSuperadmin ? (
                <TextField
                  label="Application User Code"
                  select
                  value={form.userCode}
                  onChange={handleChange('userCode')}
                  variant="standard"
                  fullWidth
                >
                  {appCodes.map((code) => (
                    <MenuItem key={code} value={code}>
                      {code}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  label="Application User Code"
                  value={form.userCode}
                  onChange={handleChange('userCode')}
                  variant="standard"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              )}
              <TextField
                label="Date"
                type="date"
                value={form.date}
                onChange={handleChange('date')}
                variant="standard"
                fullWidth
                inputRef={dateRef}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="From Code"
                select
                value={form.fromVendorCode}
                onChange={handleChange('fromVendorCode')}
                variant="standard"
                fullWidth
              >
                {vendors.map((v) => (
                  <MenuItem key={v.id} value={v.firmVendorCode}>{v.firmVendorCode} — {v.firmVendorName}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="From Bank Account Code"
                select
                value={form.fromBankCode}
                onChange={handleChange('fromBankCode')}
                variant="standard"
                fullWidth
              >
                {fromBankOptions.length > 0 ? (
                  fromBankOptions.map((b) => (
                    <MenuItem key={b.id} value={b.bankCode}>{b.bankCode} — {b.bankName}</MenuItem>
                  ))
                ) : (
                  <MenuItem value={form.fromBankCode || ''}>{form.fromBankCode || 'No bank codes'}</MenuItem>
                )}
              </TextField>

              <TextField
                label="To Code"
                select
                value={form.toVendorCode}
                onChange={handleChange('toVendorCode')}
                variant="standard"
                fullWidth
              >
                {vendors.map((v) => (
                  <MenuItem key={v.id} value={v.firmVendorCode}>{v.firmVendorCode} — {v.firmVendorName}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="To Bank Account Code"
                select
                value={form.toBankCode}
                onChange={handleChange('toBankCode')}
                variant="standard"
                fullWidth
              >
                {toBankOptions.length > 0 ? (
                  toBankOptions.map((b) => (
                    <MenuItem key={b.id} value={b.bankCode}>{b.bankCode} — {b.bankName}</MenuItem>
                  ))
                ) : (
                  <MenuItem value={form.toBankCode || ''}>{form.toBankCode || 'No bank codes'}</MenuItem>
                )}
              </TextField>

              <TextField label="Amount Paid" value={form.amountPaid} onChange={handleChange('amountPaid')} variant="standard" fullWidth />
              <TextField label="Cash Amount Received" value={form.cashReceived} onChange={handleChange('cashReceived')} variant="standard" fullWidth />

              <TextField label="Old Cash Balance" value={form.oldCashBalance} onChange={handleChange('oldCashBalance')} variant="standard" fullWidth />
              <TextField label="Applicable Commission %" value={form.commissionPct} onChange={handleChange('commissionPct')} variant="standard" fullWidth />

              <TextField label="Adjustments" value={form.adjustments} onChange={handleChange('adjustments')} variant="standard" fullWidth />
              
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  label="Transaction Type"
                  select
                  value={form.txnType}
                  onChange={handleChange('txnType')}
                  variant="standard"
                  fullWidth
                >
                  {txnTypes.map((t) => (
                    <MenuItem key={t.id} value={t.transactionType}>
                      {t.transactionType}
                    </MenuItem>
                  ))}
                </TextField>
                {isSuperAdminRole && (
                  <IconButton onClick={() => setAddTxnTypeOpen(true)} size="small" color="primary">
                    <AddIcon />
                  </IconButton>
                )}
              </Stack>

              <TextField label="Site Contact Person" value={form.siteContactPerson} onChange={handleChange('siteContactPerson')} variant="standard" fullWidth />
              <TextField label="Any Rectification" value={form.rectification} onChange={handleChange('rectification')} variant="standard" fullWidth />

              <TextField
                label="Purpose of Payment"
                value={form.purpose}
                onChange={handleChange('purpose')}
                variant="standard"
                fullWidth
                placeholder="Auto-filled from Code Combination"
                InputProps={{
                  readOnly: true,
                  endAdornment:
                    !form.purpose && form.fromVendorCode && form.toVendorCode && canCreate ? (
                      <InputAdornment position="end">
                        <AddIcon
                          fontSize="small"
                          sx={{ cursor: 'pointer' }}
                          onClick={() => {
                            setPurposeForm({
                              fromCode: form.fromVendorCode || '',
                              toCode: form.toVendorCode || '',
                              purpose: '',
                            });
                            setPurposeDialogOpen(true);
                          }}
                        />
                      </InputAdornment>
                    ) : null,
                }}
              />
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
              <Button variant="contained" type="submit" disabled={!canCreate}>Submit</Button>
            </Box>
          </Box>
        </Paper>

        <Dialog open={purposeDialogOpen} onClose={() => setPurposeDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>Add New Purpose of Payment</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="From Code"
                value={purposeForm.fromCode}
                onChange={(e) => setPurposeForm((p) => ({ ...p, fromCode: e.target.value }))}
                variant="standard"
                fullWidth
              />
              <TextField
                label="To Code"
                value={purposeForm.toCode}
                onChange={(e) => setPurposeForm((p) => ({ ...p, toCode: e.target.value }))}
                variant="standard"
                fullWidth
              />
              <TextField
                label="Purpose of Payment"
                value={purposeForm.purpose}
                onChange={(e) => setPurposeForm((p) => ({ ...p, purpose: e.target.value }))}
                variant="standard"
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setPurposeDialogOpen(false)} color="inherit">Cancel</Button>
            <Button
              onClick={async () => {
                if (!purposeForm.fromCode || !purposeForm.toCode || !purposeForm.purpose) {
                  setNotification({ open: true, message: 'From Code, To Code and Purpose required', severity: 'error' });
                  return;
                }
                if (!canCreate) {
                  setNotification({ open: true, message: 'You do not have create permission for this page', severity: 'error' });
                  return;
                }
                try {
                  const res = await fetch('/api/code-purpose', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
                    body: JSON.stringify(purposeForm),
                  });
                  const data = await res.json().catch(() => ({}));
                  if (!res.ok) throw new Error(data?.error || 'Failed to save purpose');
                  setPurposeDialogOpen(false);
                  setForm((prev) => ({ ...prev, purpose: purposeForm.purpose }));
                  setNotification({ open: true, message: 'Purpose saved', severity: 'success' });
                } catch (e) {
                  setNotification({ open: true, message: e?.message || 'Failed to save purpose', severity: 'error' });
                }
              }}
              variant="contained"
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={addTxnTypeOpen} onClose={() => setAddTxnTypeOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>Add Transaction Type</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              autoFocus
              label="New Transaction Type"
              value={newTxnType}
              onChange={(e) => setNewTxnType(e.target.value)}
              variant="standard"
              fullWidth
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setAddTxnTypeOpen(false)} color="inherit">Cancel</Button>
            <Button onClick={handleSaveTxnType} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={() => setNotification((p) => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setNotification((p) => ({ ...p, open: false }))}
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
}
